/**
 * The Wtta enters into force on this date. All milestone copy is anchored to it.
 */
export const WTTA_START_DATE = '2027-01-01';
/**
 * Companies that want to use the transitional regime must register between
 * 1 November and 31 December 2026.
 */
export const TRANSITION_REGISTRATION_START_DATE = '2026-11-01';
export const TRANSITION_REGISTRATION_END_DATE = '2026-12-31';
export function generateRoadmap(input) {
    return [
        buildPreparationMilestone(input),
        buildTrialInspectionMilestone(input),
        buildRegistrationWindowMilestone(input),
        buildEffectiveDateMilestone(input),
        buildOngoingMilestone(input),
    ];
}
function buildPreparationMilestone({ companyType, riskLevel, now, }) {
    const monthsUntilStart = monthsBetween(now, new Date(WTTA_START_DATE));
    const monthsCopy = monthsUntilStart > 0
        ? `Je hebt nog ${monthsUntilStart} ${monthsUntilStart === 1 ? 'maand' : 'maanden'} om jouw organisatie aantoonbaar op orde te brengen.`
        : 'De Wtta is inmiddels van kracht, dus deze fase is afgesloten.';
    const description = companyType === 'uitlener'
        ? `${monthsCopy} In deze fase rond je de drie zwaarste trajecten af: het SNA-traject, het op orde brengen van fiscale dossiers en de financiering van de waarborgsom van 100.000 euro. SNA-certificering kent een doorlooptijd van vier tot acht maanden, dus uitstel van de aanvraag schuift alle volgende stappen mee. Bij een readiness van ${riskLevelToPercent(riskLevel)} is het verstandig om in deze maanden ook een interne nulmeting te doen op jouw inlenersbeloning en CAO-toepassing.`
        : `${monthsCopy} Voor inleners staat in deze fase de inrichting van het toetsingsproces centraal: welke uitleners gebruik je, op welke frequentie controleer je hun toelatingsstatus, en hoe leg je dat vast. Werk de inhuurcontracten bij met Wtta-clausules en maak van het werken via een G-rekening de standaard voor al jouw nieuwe leveranciers.`;
    return {
        id: 'milestone-preparation',
        dateLabel: 'tot oktober 2026',
        startDate: toIsoDate(now),
        endDate: '2026-09-30',
        title: 'Voorbereidingsfase',
        description,
        isCritical: false,
    };
}
function buildTrialInspectionMilestone({ companyType, riskLevel, }) {
    const description = companyType === 'uitlener'
        ? `Een proef-inspectie door een onafhankelijke instelling op basis van de herziene SNA-norm legt eventuele gaten bloot vóórdat de echte toelatingsaudit plaatsvindt. ${riskLevel === 'low' ? 'Bij jouw huidige readiness is een proef-inspectie vooral een formele bevestiging dat de basis staat.' : 'Bij jouw huidige readiness is een proef-inspectie geen luxe maar een gerichte gap-analyse.'} De gangbare kosten liggen tussen 1.500 en 3.500 euro en de doorlooptijd is twee tot vier weken.`
        : 'Voor inleners is een proef-inspectie niet verplicht en niet relevant, omdat je zelf geen toelating hoeft aan te vragen. Wel is dit een goed moment om te toetsen of jouw leveranciers hun proef-inspectie hebben gepland: leveranciers die in oktober nog niet bezig zijn, lopen het risico om de aanmeldingsdeadline te missen.';
    return {
        id: 'milestone-trial-inspection',
        dateLabel: 'oktober 2026',
        startDate: '2026-10-01',
        endDate: '2026-10-31',
        title: companyType === 'uitlener' ? 'Proef-inspectie aanbevolen' : 'Toets jouw leveranciers',
        description,
        isCritical: false,
    };
}
function buildRegistrationWindowMilestone({ companyType, }) {
    const description = companyType === 'uitlener'
        ? 'In dit venster van twee maanden meld je je aan bij de Dienst Toelating Arbeidskrachten. Je levert het positieve inspectierapport aan, het bewijs van de waarborgsom en een set financiële en juridische documenten. Aanmeldingen na 31 december komen pas in behandeling nadat de wet al van kracht is, met een onderbreking van jouw bedrijfsvoering tot gevolg. De volledige documentenset staat in sectie 5.'
        : 'In deze periode wordt het toelatingsregister geopend. Vraag jouw uitleners om hun toelatingsbewijs zodra zij die ontvangen, zodat je op 1 januari 2027 niet handmatig achter elke leverancier aan moet om te checken of je nog mag blijven samenwerken.';
    return {
        id: 'milestone-registration-window',
        dateLabel: '1 november tot 31 december 2026',
        startDate: TRANSITION_REGISTRATION_START_DATE,
        endDate: TRANSITION_REGISTRATION_END_DATE,
        title: companyType === 'uitlener' ? 'Formele aanmelding' : 'Verzamel toelatingsbewijzen',
        description,
        isCritical: companyType === 'uitlener',
    };
}
function buildEffectiveDateMilestone({ companyType, }) {
    const description = companyType === 'uitlener'
        ? 'Vanaf deze datum mag je alleen arbeidskrachten ter beschikking stellen als je officieel bent toegelaten. Jouw klanten zijn vanaf nu verplicht om voor elke uitlener te controleren of de toelating actueel is. Werkt een inlener met een niet-toegelaten partij, dan riskeert die inlener boetes tot 90.000 euro per overtreding. Inleners zullen dus alleen nog zaken doen met partijen die het toelatingsbewijs op verzoek direct kunnen overleggen.'
        : 'Je bent vanaf deze datum verplicht om voor elke uitlener met wie je samenwerkt vast te stellen dat de toelating actueel is. Werk je samen met een niet-toegelaten partij, dan riskeer je boetes tot 90.000 euro per overtreding. Maak het toelatingsbewijs een standaard onderdeel van jouw inkoop- en facturatieproces.';
    return {
        id: 'milestone-effective-date',
        dateLabel: '1 januari 2027',
        startDate: WTTA_START_DATE,
        title: 'De Wtta treedt in werking',
        description,
        isCritical: true,
    };
}
function buildOngoingMilestone({ companyType, }) {
    const description = companyType === 'uitlener'
        ? 'De toelating is geen eenmalige horde. Er volgen periodieke audits waarbij je laat zien dat de norm nog steeds wordt nageleefd. De documenten en processen die je doorlopend op orde moet hebben, staan in sectie 5b. Reken op minimaal één hercertificering per twaalf maanden.'
        : 'De controleplicht geldt onbeperkt. Periodieke verificatie van toelatings-status wordt het nieuwe normaal in jouw inkoopproces. De minimale dossierset die je op verzoek van een toezichthouder moet kunnen tonen, staat in sectie 5b.';
    return {
        id: 'milestone-ongoing',
        dateLabel: 'vanaf 2027',
        startDate: '2027-01-02',
        title: 'Doorlopend toezicht',
        description,
        isCritical: false,
    };
}
function monthsBetween(from, to) {
    if (to.getTime() <= from.getTime())
        return 0;
    const years = to.getFullYear() - from.getFullYear();
    const months = to.getMonth() - from.getMonth();
    const days = to.getDate() - from.getDate();
    return Math.max(0, years * 12 + months - (days < 0 ? 1 : 0));
}
function toIsoDate(d) {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}
function riskLevelToPercent(level) {
    switch (level) {
        case 'low':
            return 'meer dan 75%';
        case 'medium':
            return 'tussen de 50 en 75%';
        case 'high':
            return 'onder de 50%';
    }
}
//# sourceMappingURL=roadmap.js.map